import { config } from "@config/config";
import { DocumentPlusIcon, PlusIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { Button, Dialog, FileInstance, Small } from "@fronterahealth/frontera-ui-components";

import {
  useCreateGoalsAndTargetsMutation,
  useStartTreatmentPlanUploadParsingMutation,
  useTreatmentPlanUploadParsingResultsQuery,
} from "@api/graphql/types-and-hooks";
import { DiscardChangesDialog } from "@components/TreatmentPlan/DiscardChangesDialog";
import { GoalExplorer } from "@components/TreatmentPlan/GoalExplorer/GoalExplorer";
import { GoalItemType, areGoalsEqual, rawAIparsedTreatmentPlanDataToGoals } from "@components/TreatmentPlan/utils";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { UploadTreatmentPlan } from "@pages/ClientProgramming/UploadTreatmentPlan/UploadTreatmentPlan";

type DialogState = "closed" | "upload" | "editor";

export const AddProgrammingDialogButton = () => {
  const [dialogState, setDialogState] = useState<DialogState>("closed");
  const [uploadedFiles, setUploadedFiles] = useState<FileInstance[] | null>(null);
  const hasFiles = (uploadedFiles?.length || 0) >= 1;

  const [goalsList, setGoalsList] = useState<GoalItemType[]>([]);
  const [initialGoalList, setInitialGoalList] = useState<GoalItemType[]>([]);

  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);

  const { clientId } = useParams<{ clientId: string }>();
  const queryClient = useQueryClient();
  const {
    mutate: startTreatmentPlanUploadParsing,
    data: startData,
    isPending: isStartingParsing,
    reset: resetParsing,
  } = useStartTreatmentPlanUploadParsingMutation({
    onError: (error) => {
      console.error("Failed to start treatment plan upload parsing", error);
      notifyError("Failed to start treatment plan upload parsing. Please try again.");
      // Reset to allow retry
      setTimeout(() => resetParsing(), 3000);
    },
  });

  const {
    mutate: importGoalsAndTargets,
    isPending: isImportLoading,
    reset: resetImport,
  } = useCreateGoalsAndTargetsMutation({
    onSuccess: () => {
      // Invalidate the goals query to refetch the updated data
      queryClient.invalidateQueries({ queryKey: ["goals", clientId] });
      notifySuccess("Treatment plan imported successfully!");
      // not sure if this is necessary, but reset state and close dialog
      setGoalsList([]);
      setInitialGoalList([]);
      setUploadedFiles(null);
      resetParsing();
      resetImport();
      setDialogState("closed");
    },
    onError: (error) => {
      console.error("Failed to import treatment plan", error);
      notifyError("Failed to import treatment plan. Please try again.");
      // Reset to allow retry
      setTimeout(() => resetImport(), 3000);
    },
  });

  const {
    data: pollingData,
    error,
    isLoading: isPollingLoading,
  } = useTreatmentPlanUploadParsingResultsQuery(
    {
      id: startData?.createParsedTreatmentPlanFileData?.record?.id || "<missing-id>",
    },
    {
      enabled: !!startData?.createParsedTreatmentPlanFileData?.record?.id,
      refetchInterval: (query) => {
        const status = query.state.data?.parsedTreatmentPlanFileDataById?.status;
        if (status === "COMPLETED" || status === "FAILED") {
          return false;
        }
        return config.REPORT_POLLING_INTERVAL;
      },
    },
  );
  useEffect(() => {
    if (error) {
      console.error("Failed to update the status of treatment plan parsing", error);
      notifyError("Failed to retrieve parsing results. Please try again.");
      // Reset parsing state on polling error
      resetParsing();
    }
  }, [error, resetParsing]);

  const isPollingForParsedTreatmentPlanUploadData =
    isPollingLoading ||
    Boolean(
      startData?.createParsedTreatmentPlanFileData?.record?.id &&
        pollingData?.parsedTreatmentPlanFileDataById?.status !== "COMPLETED" &&
        pollingData?.parsedTreatmentPlanFileDataById?.status !== "FAILED",
    );

  useEffect(() => {
    const status = pollingData?.parsedTreatmentPlanFileDataById?.status;

    if (!status) return;

    if (status === "FAILED") {
      notifyError("AI parsing failed. You can still add programming manually.");
      resetParsing();
      return;
    }

    if (status === "COMPLETED") {
      const goals = rawAIparsedTreatmentPlanDataToGoals(pollingData?.parsedTreatmentPlanFileDataById?.data);
      if (!goals || !goals.length) {
        notifyError("Failed to parse treatment plan data. Please try uploading again.");
        resetParsing();
      } else {
        setGoalsList(goals);
        setInitialGoalList(goals);
        setDialogState("editor");
      }
    }
  }, [pollingData?.parsedTreatmentPlanFileDataById, resetParsing]);

  // reset data when the dialog opens
  useEffect(() => {
    if (dialogState === "upload") {
      setInitialGoalList([]);
      setGoalsList([]);
      setUploadedFiles(null);
      resetParsing();
      resetImport();
    }
  }, [dialogState, resetParsing, resetImport]);

  const handleCloseRequest = (targetState: DialogState) => {
    if (isStartingParsing || isPollingForParsedTreatmentPlanUploadData || isImportLoading) {
      // we are awaiting results - warn the user
      setIsDiscardDialogOpen(true);
    } else if (!areGoalsEqual(initialGoalList, goalsList)) {
      // we have unsaved data - warn the user
      setIsDiscardDialogOpen(true);
    } else {
      setDialogState(targetState);
    }
  };

  const handleDiscardChanges = () => {
    setGoalsList(structuredClone(initialGoalList));
    setDialogState("closed");
    setIsDiscardDialogOpen(false);
  };

  return (
    <>
      <Button
        appearance="secondary"
        text={
          <div className="flex flex-raw justify-between w-full gap-2">
            <DocumentPlusIcon className="w-5 h-5" /> Add Programming
          </div>
        }
        onClick={() => setDialogState("upload")}
      />
      {dialogState === "upload" && (
        <Dialog
          key="upload_dialog"
          title={
            <Small displayType="loud" colorType="primary">
              Add Programming
            </Small>
          }
          open={true}
          setOpen={(val) => {
            if (!val) {
              handleCloseRequest("closed");
            }
          }}
          primaryButton={{
            text: "Upload",
            disabled: !hasFiles || isPollingForParsedTreatmentPlanUploadData || isStartingParsing || !clientId,
            onClick: () =>
              startTreatmentPlanUploadParsing({
                learnerId: clientId!,
                input: { fileIds: uploadedFiles?.map((f) => f.fileId) || [] },
              }),
          }}
          secondaryButton={{
            text: "Cancel",
            onClick: () => handleCloseRequest("closed"),
          }}
          size="3xl"
          overflowBehavior="scroll"
          showDividers
          tertiaryButton={{
            disabled: isPollingForParsedTreatmentPlanUploadData || isStartingParsing,
            text: (
              <div className="flex gap-1 items-center">
                <PlusIcon className="h-4 w-4" />
                Program Manually
              </div>
            ),
            onClick: () => {
              setDialogState("editor");
            },
            className: "p-0!",
          }}
        >
          {isPollingForParsedTreatmentPlanUploadData || isStartingParsing ? (
            <div className="animate-pulse py-2 mb-2">
              <div className="bg-limestone-200 h-40 rounded-lg p-4 flex flex-col justify-center items-center">
                <div className="text-lg font-medium mb-2">
                  {isStartingParsing ? "Starting AI parsing..." : "AI is analyzing your document..."}
                </div>
                <div className="text-sm text-gray-600">This may take a few moments</div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col">
              <UploadTreatmentPlan setUploadedFiles={setUploadedFiles} />
            </div>
          )}
        </Dialog>
      )}

      {dialogState === "editor" && (
        <Dialog
          title={
            <Small displayType="loud" colorType="primary">
              Add Programming
            </Small>
          }
          showCloseButton={false}
          open={true}
          setOpen={() => {}}
          primaryButton={{
            text: isImportLoading ? "Importing..." : "Import",
            onClick: () => {
              if (!clientId) {
                notifyError("Client ID is missing. Please try again.");
                return;
              }
              if (!goalsList.length) {
                notifyError("No goals to import. Please add at least one goal.");
                return;
              }
              importGoalsAndTargets({
                learnerId: clientId,
                goals: goalsList.map((g) => ({
                  goalName: g.goalName,
                  goalDescription: g.goalDescription,
                  targets: g.targets?.map((t) => ({
                    targetName: t.targetName,
                    targetDescription: t.targetDescription,
                  })),
                })),
              });
            },
            disabled: isImportLoading || !goalsList.length || !clientId,
          }}
          secondaryButton={{
            text: "Cancel",
            onClick: () => handleCloseRequest("closed"),
            disabled: isImportLoading,
          }}
          size="7xl"
          showDividers
          tertiaryButton={{
            text: "Back",
            onClick: () => handleCloseRequest("upload"),
            className: "p-0!",
            disabled: isImportLoading,
          }}
          bodyClassName="p-0! flex-1"
          key="editor_dialog"
        >
          <GoalExplorer title="Goals" goalsList={goalsList} setGoalsList={setGoalsList} />
        </Dialog>
      )}

      <DiscardChangesDialog
        open={isDiscardDialogOpen}
        setOpen={setIsDiscardDialogOpen}
        handleDiscardChanges={handleDiscardChanges}
      />
    </>
  );
};
